import { useEffect } from 'react';
import { json, type LoaderFunctionArgs } from '@remix-run/node';
import { useLoaderData, useLocation } from '@remix-run/react';
import { getPreloadImageLink } from '~/modules/images';
import {
    mergeMeta,
    createMetaTitle,
    createMetaDescription,
    fallbackOGSocialImage,
    createOGTags,
    getCanonicalHreflang,
} from '@modules/seo';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { createVideoSchema } from '~/modules/seo/schema/video-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import Container from '@modules/project/component/container/Container';
import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import makeProject from '@modules/project/util/make-project';
import getProjectQuery from '@modules/project/util/get-project-query';
import getFeaturedProductsQuery from '@modules/project/util/get-featured-products-query';
import getFeaturedProfessionalsQuery from '@modules/project/util/get-featured-professionals-query';
import getFeaturedProjectsQuery from '@modules/project/util/get-featured-projects-query';
import makeFeaturedProducts from '@modules/project/util/make-featured-products.server';
import makeFeaturedProjects from '@modules/project/util/make-featured-projects';
import makeFeaturedProfessionals from '@modules/project/util/make-featured-professionals';
import * as S from '@modules/project/component/container/Container.style';
import DetailModules from '~/modules/page-builder/component/detail/detail-modules/DetailModules';
import { getBMPagePath } from '~/modules/root/util/get-bm-page-path';
import { ProductTaggingContextProvider } from '~/modules/product-tagging/context';
import { getPermissions, useEditPermissions } from '~/modules/user';
import { PermissionEnum } from 'generated/graphql';
import getMemberRecentlyTagged from '@modules/product-tagging/api/get-member-recently-tagged.server';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import ProjectPageContent from '~/modules/project/component/content/ProjectPageContent';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { PaintPickerContextProvider as PaintPickerContextProviderV1 } from '@modules/paint-picker/context/PaintPickerContext';
import { PaintPickerContextProvider as PaintPickerContextProviderV2 } from '@modules/paint-picker-new/context/PaintPickerContext';
import { unsafeVariant_DO_NOT_USE } from '~/utils/unsafeVariant';
import { useRefreshOnRotate } from '@modules/root/util/use-refresh-on-rotate';
import { ensure } from '@modules/design-board/util/ensure.server';
import getProjectBuilderModulesQuery from '@modules/project/util/get-project-builder-modules.query';
import { useSetEditMode } from '@modules/root/hook/use-set-edit-mode';
import { getPrimaryCTA } from '@modules/professional/util/professional-cta-helper';
import { PrimaryCtaEnum } from 'generated/graphql';
import { adsRequest } from '~/modules/adverts/api/ads-request.server';
import getModuleWithAds from '@modules/project/util/get-module-with-ads';
import { useMemo, useRef } from 'react';
import { createWarningMessages } from '@modules/root/hook/use-warning-messages';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getProjectPlacements } from '~/modules/adverts/utils/project-placement-helper.server';
import { makeProjectItemData } from '~/modules/adverts/utils/get-item-data.server';
import { noIndexCrossCountry } from '~/modules/seo/util/no-index-cross-country';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { useFeature, type APHandle } from '~/modules/root';
import { useProjectPreviewRouteData } from '@modules/project/hook/use-project-preview-route-data';
import { getSession } from '~/modules/root/util/session.server';
import dayjs from 'dayjs';
import { ModulesContextProvider } from '~/modules/page-builder/context/modules-context';
import { ProjectIsland } from '@modules/project/component/project-island/ProjectIsland';
import { ClientOnly } from 'remix-utils';
import type { PageBuilderModuleType } from '@modules/page-builder/types';
import type { ProjectAd } from '@modules/adverts/type';
import ProjectPageHeader from '~/modules/project/component/page-header/ProjectPageHeader';
import { useSetAtom } from 'jotai';
import { setCloneCountryStateAtom } from '~/state/ui-state-clone-country-link';
import { PageBottomSection } from '~/ui-rocco/pages/shared/PageBottomSection';
import { getProjectTeamMembers } from '~/modules/professional/api/get-project-team-members.server';
import { useOpenLogin } from '~/utils-rocco/hooks/open-login-modal';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;

        const { rootData } = getRouteData(matches);

        const project = data?.project;
        const projectTitle =
            project?.Title &&
            project?.Professional?.Title &&
            `${project?.Title} by ${project?.Professional?.Title}`;

        const title = createMetaTitle(props, [
            projectTitle,
            data?.localisation?.Title,
        ]);

        const description = createMetaDescription([
            data?.localisation?.Description,
            data?.project?.Content,
        ]);

        const fallbackSocialImage = fallbackOGSocialImage(
            props,
            data?.project?.BannerImages?.[0]?.LibraryImageFile
                ?.DetailBannerStandard?.Desktop
        );

        const videoSchemas =
            project?.Modules.filter(
                (module) =>
                    module.presetType === 'SingleVideoModule' ||
                    module.presetType === 'VideoParagraphModule'
            )
                .flatMap((module) => module.columns)
                .map((column) => {
                    const { video } = column;
                    // take project's PublishDate if we don't have any video dateCreated
                    const publishDate =
                        video?.dateCreated ??
                        (data?.project?.PublishDate
                            ? dayjs(
                                  data.project.PublishDate,
                                  'D-MM-YY'
                              ).format()
                            : ' ');

                    return createVideoSchema({
                        name: video?.title ?? data?.project?.Title ?? '',
                        publishDate: publishDate,
                        professionalName: project.Professional.Title ?? '',
                        url: video?.videoURL ?? '',
                        thumbnailUrl: video?.coverImageThumbnail ?? '',
                        description: video?.title || description,
                    });
                }) ?? [];

        const ldSchema = createLDSchema(videoSchemas);

        const bannerImage = data?.project?.Banner?.imageUrl;

        const preloadImages = [
            ...getPreloadImageLink(bannerImage, '', getImagesSizes(1820)),
        ];

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                title,
                description,
                meta: props,
                facebookSocialImage:
                    data?.project?.FacebookPreviewImage ?? fallbackSocialImage,
                type: 'website',
            }),
            ...getCanonicalHreflang(props, {
                professionalCountry: data?.project?.Professional?.Country,
            }),
            ...ldSchema,
            ...preloadImages,
            ...noIndexCrossCountry(
                data?.project?.Professional?.Country,
                rootData?.localisation?.country,
                data?.localisation
            ),
        ];
    }
);

export const handle: APHandle = {
    fadeTopNavAfterPosition: 200,
    hideContactArchiPro: true,
};

export const loader = async (args: LoaderFunctionArgs) => {
    const { params, context, request } = args;
    ensure(!!params.urlSegment, 'A project URL segment is required');
    const { appDisplayMode, user } = context;
    const isMobile = appDisplayMode === 'mobile';

    const projectPromise = getProjectQuery(args, false);
    const [
        projectResult,
        featuredProductsResult,
        featuredProfessionalsResult,
        featuredProjectsResult,
        memberRecentlyTagged,
        localisation,
        projectBuilderModulesResult,
        projectAds,
    ] = await Promise.all([
        projectPromise,
        getFeaturedProductsQuery(args),
        getFeaturedProfessionalsQuery(args),
        getFeaturedProjectsQuery(args),
        getMemberRecentlyTagged(args),
        getPageLocalisation(args),
        getProjectBuilderModulesQuery(args),
        (async () => {
            // waterfall request to get the project data and feed it to the Ads API.
            // Avoid duplicated heavy ORM queries for the project data in the Ads BE.
            const projectResponse = await projectPromise;
            unsafeVariant_DO_NOT_USE(projectResponse);

            const projectItemData = await makeProjectItemData(
                args,
                projectResponse
            );

            return adsRequest({
                type: 'ProjectPlacementGroup',
                requestUrl: request.url,
                item: projectItemData,
                ...args.context,
            });
        })(),
    ]);

    unsafeVariant_DO_NOT_USE(featuredProductsResult);
    unsafeVariant_DO_NOT_USE(featuredProfessionalsResult);
    unsafeVariant_DO_NOT_USE(featuredProjectsResult);
    unsafeVariant_DO_NOT_USE(projectResult);

    ensure(!!projectBuilderModulesResult, 'Project builder modules not found');

    const project = await makeProject(
        args,
        projectResult,
        projectBuilderModulesResult,
        user
    );

    const [
        featuredProducts,
        featuredProfessionals,
        featuredProjects,
        projectTeamMembers,
    ] = await Promise.all([
        makeFeaturedProducts(args, featuredProductsResult, project.Title, user),
        makeFeaturedProfessionals(args, featuredProfessionalsResult, project),
        makeFeaturedProjects(args, featuredProjectsResult, project),
        getProjectTeamMembers({
            projectId: String(project.ID),
            ...args.context,
        }),
    ]);

    const primaryCTA = getPrimaryCTA({
        primaryCTA: PrimaryCtaEnum.AutoOptimise,
        pageType: 'project',
    });

    const showPageNotLiveWarning = getPermissions(
        user,
        PermissionEnum.BmStaffView,
        project.Professional.ID
    );

    // Create warning message for the WarningBanner
    const warningMessages = createWarningMessages(
        project.Professional?.Country || 'NZ',
        context.localisation.country,
        !project.IsLive || project.Disable || project.Professional?.Disable,
        'project',
        false,
        {
            showPageNotLiveWarning: showPageNotLiveWarning,
            isAPStaff: getPermissions(user, PermissionEnum.BmStaffView),
        }
    );

    const ads = projectAds ? getProjectPlacements(projectAds, isMobile) : [];

    const session = await getSession(request);

    return json(
        {
            project,
            featuredProducts,
            featuredProfessionals,
            featuredProjects,
            memberRecentlyTagged,
            memberPermissions: {
                canCreateTag: getPermissions(
                    user,
                    PermissionEnum.BmProductTaggingCreate,
                    project.Professional.ID
                ),
                canDeleteTag: getPermissions(
                    user,
                    PermissionEnum.BmProductTaggingDelete,
                    project.Professional.ID
                ),
                hasMarketingView: getPermissions(
                    user,
                    PermissionEnum.BmMarketingView,
                    project.Professional.ID
                ),
            },
            localisation,
            primaryCTA,
            ads,
            warningMessages,
            projectTeamMembers: projectTeamMembers ?? [],
            pageTheme:
                context.device.type === 'desktop' ? 'none' : 'transparent',
        },
        {
            headers: await session.getHeaders(),
        }
    );
};

const ModuleAds = ({
    modules,
    ads,
}: {
    modules: PageBuilderModuleType[];
    ads: ProjectAd[];
}) => {
    const location = useLocation();

    const hideAd = location.state?.hideAd;
    // Keep the first value returned by loader to avoid changing when loader revalidate
    const adsMemoized = useLocationMemo(
        hideAd || typeof document === 'undefined' ? [] : ads
    );
    const modulesWithAds = useMemo(
        () => getModuleWithAds(modules, adsMemoized),
        [modules, adsMemoized]
    );

    return (
        <ClientOnly fallback={<DetailModules modules={modules} />}>
            {() => <DetailModules modules={modulesWithAds} />}
        </ClientOnly>
    );
};

const ProjectPage = () => {
    const { desktop: isDesktop, mobile: isMobile } = useAppDisplayModeContext();
    useRefreshOnRotate();
    const { memberRecentlyTagged, memberPermissions, ads, primaryCTA } =
        useLoaderData<typeof loader>();

    const project = useProjectPreviewRouteData();
    const { setCategory } = useCurrentCategory();

    const setCountryData = useSetAtom(setCloneCountryStateAtom);

    useEffect(() => {
        setCountryData({
            urlSegment: project?.URLSegment,
            currentCountry: project?.Professional?.Country,
            pageType: 'project',
            associatedPages: project?.AssociatedPages.edges.length
                ? project?.AssociatedPages
                : project?.Professional?.ProfessionalAssociatedPages,
        });
    }, [project, setCountryData]);

    // Set the current category for header navigation
    useEffect(() => {
        setCategory('Projects');
    }, [setCategory]);

    const { css } = useStyles();
    const { canCreateTag, canDeleteTag, hasMarketingView } = memberPermissions;
    const canDownloadBannerImage = !isMobile && hasMarketingView;
    const { canEditProfile } = useEditPermissions(project.Professional.ID);
    const projectEditPath = getBMPagePath(
        project.Professional.URLSegment,
        'projects',
        project.URLSegment
    );
    useSetEditMode(projectEditPath, canEditProfile);

    const PickerContext = useFeature('resene_color_picker_2024')
        ? PaintPickerContextProviderV2
        : PaintPickerContextProviderV1;

    const modulesContainerRef = useRef<HTMLDivElement>(null);
    const openLogin = useOpenLogin();

    return (
        <>
            <Container>
                <PickerContext
                    containerRef={modulesContainerRef}
                    projectId={project.ID}
                    projectLink={project.Link}
                    bannerUrl={project.Banner.imageUrl}
                    isDesktop={isDesktop}
                    isEnabled={project.Modules.length > 0}
                >
                    <ProjectPageHeader
                        canDownloadBannerImage={canDownloadBannerImage}
                    />

                    <ProductTaggingContextProvider
                        canCreateTag={isDesktop && canCreateTag}
                        canDeleteTag={canDeleteTag}
                        recentlyTaggedProducts={memberRecentlyTagged.products}
                        recentlyTaggedProfessionals={
                            memberRecentlyTagged.professionals
                        }
                    >
                        <ModulesContextProvider>
                            <div
                                ref={modulesContainerRef}
                                className="mt-14 md:mb-24 md:mt-24"
                            >
                                <ModuleAds
                                    modules={project.Modules}
                                    ads={ads}
                                />
                            </div>
                        </ModulesContextProvider>
                    </ProductTaggingContextProvider>
                    <div className={css(S.Section)}>
                        <ProjectPageContent />
                    </div>

                    <div className="my-10 flex flex-col gap-10 md:mb-[12.8125rem] md:mt-20 md:gap-[13.75rem]">
                        <PageBottomSection openLogin={openLogin} />
                    </div>
                </PickerContext>
            </Container>

            <ProjectIsland project={project} primaryCTA={primaryCTA} />
        </>
    );
};

export default ProjectPage;
